
import React from 'react';
import { useTestimonials } from '@/hooks/useTestimonials';
import TestimonialCarousel from './testimonials/TestimonialCarousel';
import { Loader2 } from 'lucide-react';

type TestimonialSectionProps = {
  cityFilter?: string;
  citySlug?: string;
  cityName?: string;
};

const TestimonialSection = ({
  cityFilter,
  citySlug = 'new-smyrna-beach',
  cityName = 'New Smyrna Beach'
}: TestimonialSectionProps) => {
  // Fetch testimonials from Supabase for Elevation Electrical
  const { data: testimonials, isLoading } = useTestimonials(null, 20);

  // Phase 2A: partner testimonials removed. The unified GoogleReviewsCarousel
  // above the fold is now the single canonical reviews surface; per-city
  // testimonials below are customer-only.
  const partnerTestimonials: never[] = [];

  // Filter customer testimonials by city if a filter is provided
  const customerTestimonials = React.useMemo(() => {
    if (!testimonials) return [];
    
    // Map Supabase testimonials to match the expected format
    const mappedTestimonials = testimonials.map((t, index) => ({
      id: index + 1, // Generate sequential IDs
      name: t.name,
      text: t.content,
      rating: t.rating,
      location: cityName, // Use city name for now
      service: 'Electrical Services', // Default service
      image: undefined
    }));

    if (cityFilter) {
      return mappedTestimonials.slice(0, 6);
    }
    return mappedTestimonials.slice(0, 6);
  }, [testimonials, cityFilter, cityName]);

  if (isLoading) {
    return (
      <section className="section bg-lightgray">
        <div className="container-custom">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section bg-lightgray">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="mb-4">What Our Customers Say</h2>
          <p className="max-w-3xl mx-auto text-navy/80">
            Verified customer testimonials from {cityName} and the surrounding service area.
          </p>
        </div>
        
        <TestimonialCarousel 
          customerTestimonials={customerTestimonials}
          partnerTestimonials={partnerTestimonials}
        />
      </div>
    </section>
  );
};

export default TestimonialSection;
